/**
 * SpawnBatchGroupedTable Component
 *
 * Grouped table view for spawn batches, organized by strain with collapsible headers.
 * Replaces both SpawnStockSummary and the flat batch table.
 * Part of US-P2-002: Spawn Production Tracking
 */

import { useState, useMemo, Fragment } from 'react'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  TableFooter
} from '@/components/ui/table'
import { Card } from '@/components/ui/card'
import {
  ChevronDown,
  ChevronRight,
  ChevronsDownUp,
  ChevronsUpDown,
  Play,
  Beaker
} from 'lucide-react'
import { parseDateString, toLocalDateString } from '@/utils/dateUtils'

function formatDate(dateStr) {
  if (!dateStr) return '-'
  return parseDateString(dateStr).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  })
}

/**
 * Calculate expected ready date for a batch based on available data:
 * 1. EWMA mean from process_statistics (most accurate)
 * 2. Manually set expected_ready_date
 * 3. Strain typical_spawn_days fallback
 */
function getExpectedReady(batch) {
  const inocDate = batch.spawn_start || batch.inoculation_date
  if (!inocDate) return null

  // Priority 1: EWMA stats from process_statistics
  if (batch.strain_spawn_stats?.ewma_mean && batch.strain_spawn_stats.sample_count > 0) {
    const days = Math.round(batch.strain_spawn_stats.ewma_mean)
    const date = new Date(inocDate)
    date.setDate(date.getDate() + days)
    return { date: toLocalDateString(date), source: 'ewma' }
  }

  // Priority 2: Manually set expected_ready_date
  if (batch.expected_ready_date) {
    return { date: batch.expected_ready_date, source: 'manual' }
  }

  // Priority 3: Strain typical_spawn_days
  if (batch.strain?.typical_spawn_days) {
    const days = batch.strain.typical_spawn_days
    const date = new Date(inocDate)
    date.setDate(date.getDate() + days)
    return { date: toLocalDateString(date), source: 'typical' }
  }

  return null
}

export default function SpawnBatchGroupedTable({
  batches,
  onBatchClick,
  onStartShadow
}) {
  // Initialize all strains as expanded
  const [expandedStrains, setExpandedStrains] = useState(() => {
    const ids = new Set()
    for (const b of batches) {
      ids.add(b.strain_id)
    }
    return ids
  })

  // Keep expanded set in sync when batches change
  useMemo(() => {
    setExpandedStrains(prev => {
      const currentStrainIds = new Set(batches.map(b => b.strain_id))
      // Add any new strain IDs not yet in the set
      let changed = false
      const next = new Set(prev)
      for (const id of currentStrainIds) {
        if (!next.has(id)) {
          next.add(id)
          changed = true
        }
      }
      return changed ? next : prev
    })
  }, [batches])

  const allExpanded = useMemo(() => {
    const strainIds = new Set(batches.map(b => b.strain_id))
    return strainIds.size > 0 && expandedStrains.size >= strainIds.size
  }, [batches, expandedStrains])

  const toggleAll = () => {
    if (allExpanded) {
      setExpandedStrains(new Set())
    } else {
      setExpandedStrains(new Set(batches.map(b => b.strain_id)))
    }
  }

  const toggleStrain = (strainId) => {
    setExpandedStrains(prev => {
      const next = new Set(prev)
      if (next.has(strainId)) {
        next.delete(strainId)
      } else {
        next.add(strainId)
      }
      return next
    })
  }

  // Group batches by strain, sort, and calculate summaries
  const { strainGroups, grandTotal } = useMemo(() => {
    const groupMap = {}

    for (const batch of batches) {
      const sid = batch.strain_id
      if (!groupMap[sid]) {
        groupMap[sid] = {
          strainId: sid,
          strainName: batch.strain_name || batch.strain?.name || 'Unknown',
          batches: [],
          totalBags: 0,
          colonisedBags: 0,
          colonisingBags: 0,
          availableBags: 0,
          contaminatedBags: 0,
          totalMassKg: 0,
          availableMassKg: 0
        }
      }
      groupMap[sid].batches.push(batch)
      groupMap[sid].totalMassKg += (batch.bag_count || 0) * (batch.bag_weight_kg || 0)

      // Accumulate bag counts from bag_summary or fall back to bag_count
      if (batch.bag_summary) {
        const availBags = batch.bag_summary.colonised_available ?? batch.bag_summary.available ?? 0
        groupMap[sid].totalBags += batch.bag_summary.total
        groupMap[sid].colonisedBags += availBags
        groupMap[sid].availableBags += batch.bag_summary.available
        groupMap[sid].contaminatedBags += (batch.bag_summary.contaminated || 0)
        // Colonising = available bags that aren't yet colonised
        groupMap[sid].colonisingBags += (batch.bag_summary.available - availBags)
        groupMap[sid].availableMassKg += availBags * (batch.bag_weight_kg || 0)
      } else {
        // Fallback: use bag_count for total, derive from status
        const count = batch.bag_count || 0
        groupMap[sid].totalBags += count
        if (batch.status === 'colonised' || batch.status === 'storage') {
          groupMap[sid].colonisedBags += count
          groupMap[sid].availableMassKg += count * (batch.bag_weight_kg || 0)
        } else if (batch.status === 'colonising') {
          groupMap[sid].colonisingBags += count
        }
      }
    }

    // Sort groups alphabetically by strain name
    const groups = Object.values(groupMap).sort((a, b) =>
      a.strainName.localeCompare(b.strainName)
    )

    // Sort batches within each group: colonised first, then by inoculation_date ascending
    for (const group of groups) {
      group.batches.sort((a, b) => {
        const aCol = (!a.is_shadow && (a.status === 'colonised' || a.status === 'storage')) ? 0 : 1
        const bCol = (!b.is_shadow && (b.status === 'colonised' || b.status === 'storage')) ? 0 : 1
        if (aCol !== bCol) return aCol - bCol
        const dateA = new Date(a.spawn_start || a.inoculation_date || 0)
        const dateB = new Date(b.spawn_start || b.inoculation_date || 0)
        return dateA - dateB
      })
    }

    // Grand total (including total mass)
    const grand = groups.reduce(
      (acc, g) => ({
        totalBags: acc.totalBags + g.totalBags,
        colonisedBags: acc.colonisedBags + g.colonisedBags,
        colonisingBags: acc.colonisingBags + g.colonisingBags,
        availableMassKg: acc.availableMassKg + g.availableMassKg
      }),
      { totalBags: 0, colonisedBags: 0, colonisingBags: 0, availableMassKg: 0 }
    )
    grand.totalMassKg = batches.reduce(
      (sum, b) => sum + (b.bag_count || 0) * (b.bag_weight_kg || 0), 0
    )

    return { strainGroups: groups, grandTotal: grand }
  }, [batches])

  if (batches.length === 0) {
    return (
      <Card>
        <div className="flex flex-col items-center gap-2 py-12 text-content-secondary">
          <Beaker className="h-8 w-8 text-content-tertiary" />
          <span>No spawn batches match your filters</span>
        </div>
      </Card>
    )
  }

  return (
    <Card>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-8 pl-2">
              <Button variant="ghost" size="sm" onClick={toggleAll} className="text-xs gap-1 h-8 w-8 p-0">
                {allExpanded ? (
                  <ChevronsDownUp className="h-3.5 w-3.5" />
                ) : (
                  <ChevronsUpDown className="h-3.5 w-3.5" />
                )}
              </Button>
            </TableHead>
            <TableHead className="w-[15%]">Code</TableHead>
            <TableHead className="w-[11%]">Bags</TableHead>
            <TableHead className="w-[11%]">Available</TableHead>
            <TableHead className="w-[17%]">Spawn Start</TableHead>
            <TableHead className="w-[17%]">Available/Total</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {strainGroups.map(group => {
            const isExpanded = expandedStrains.has(group.strainId)

            return (
              <Fragment key={group.strainId}>
                {/* Strain group header */}
                <TableRow
                  className="bg-surface-secondary/60 hover:bg-surface-secondary cursor-pointer"
                  onClick={() => toggleStrain(group.strainId)}
                >
                  <TableCell colSpan={6} className="py-2 pl-2 pr-4">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        {isExpanded ? (
                          <ChevronDown className="h-4 w-4 text-content-secondary" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-content-secondary" />
                        )}
                        <span className="font-medium">{group.strainName}</span>
                      </div>
                      <span className="text-sm text-content-secondary tabular-nums">
                        {group.colonisedBags}/{group.totalBags} &middot; {group.availableMassKg.toFixed(1)}/{group.totalMassKg.toFixed(1)}kg
                      </span>
                    </div>
                  </TableCell>
                </TableRow>

                {/* Batch rows */}
                {isExpanded && group.batches.map(batch => {
                  const code = batch.code || batch.batch_code
                  const isShadow = batch.is_shadow
                  const expectedReady = getExpectedReady(batch)

                  return (
                    <TableRow
                      key={batch.id}
                      className={isShadow ? 'text-content-secondary' : ''}
                    >
                      {/* Action: play button for shadow batches */}
                      <TableCell className="pl-2">
                        {isShadow && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onStartShadow(batch)}
                            className="text-odin-blue hover:bg-odin-blue/10 h-8 w-8 p-0"
                          >
                            <Play className="h-4 w-4" />
                          </Button>
                        )}
                      </TableCell>

                      {/* Code (clickable) */}
                      <TableCell className="font-mono font-medium">
                        <button
                          onClick={() => onBatchClick(batch)}
                          className="hover:underline hover:text-odin-blue cursor-pointer text-left"
                        >
                          {code}
                        </button>
                      </TableCell>

                      {/* Bags */}
                      <TableCell className="tabular-nums">
                        {batch.bag_count} &times; {batch.bag_weight_kg}kg
                      </TableCell>

                      {/* Available */}
                      <TableCell className="tabular-nums">
                        {isShadow ? '-' : (() => {
                          if (batch.bag_summary) {
                            const available = batch.bag_summary.colonised_available ?? batch.bag_summary.available ?? 0
                            return `${available}/${batch.bag_summary.total}`
                          }
                          return (batch.status === 'colonised' || batch.status === 'storage')
                            ? `${batch.bag_count}/${batch.bag_count}`
                            : `0/${batch.bag_count}`
                        })()}
                      </TableCell>

                      {/* Spawn Start */}
                      <TableCell>
                        {formatDate(batch.spawn_start || batch.inoculation_date)}
                      </TableCell>

                      {/* Expected Ready */}
                      <TableCell>
                        {isShadow ? '-' : expectedReady ? formatDate(expectedReady.date) : '-'}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </Fragment>
            )
          })}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TableCell className="pl-2" />
            <TableCell className="font-medium">Total</TableCell>
            <TableCell />
            <TableCell />
            <TableCell />
            <TableCell className="tabular-nums font-medium">
              {grandTotal.colonisedBags}/{grandTotal.totalBags} · {grandTotal.availableMassKg.toFixed(1)}/{grandTotal.totalMassKg.toFixed(1)}kg
            </TableCell>
          </TableRow>
        </TableFooter>
      </Table>
    </Card>
  )
}
